/**
 * WebAuthn Ceremony Helper (Pure Native Browser APIs)
 * Zero external dependencies. Fully compatible with RFC 4648 and W3C WebAuthn Level 3.
 */
import { httpClient } from '@/api';

// --- Base64URL Encoding & Decoding ---

export function bufferToBase64URL(buffer) {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = window.btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64URLToBuffer(base64url) {
  if (!base64url || typeof base64url !== 'string') return new ArrayBuffer(0);
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  if (pad === 2) base64 += '==';
  else if (pad === 3) base64 += '=';
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// --- Browser Compatibility Checks ---

export function isWebAuthnSupported() {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    !!window.PublicKeyCredential &&
    !!navigator?.credentials?.create &&
    !!navigator?.credentials?.get
  );
}

export async function isPlatformAuthenticatorAvailable() {
  if (!isWebAuthnSupported()) return false;
  if (typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable !== 'function') {
    return false;
  }
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// --- Options Transformers ---

export function prepareCreationOptions(rawOptions) {
  if (typeof window !== 'undefined' && typeof window.PublicKeyCredential?.parseCreationOptionsFromJSON === 'function') {
    try {
      return { publicKey: window.PublicKeyCredential.parseCreationOptionsFromJSON(rawOptions) };
    } catch {
      // Fallback to manual decoding below
    }
  }
  const options = { ...rawOptions };
  if (typeof options.challenge === 'string') {
    options.challenge = base64URLToBuffer(options.challenge);
  }
  if (options.user && typeof options.user.id === 'string') {
    options.user = {
      ...options.user,
      id: base64URLToBuffer(options.user.id),
    };
  }
  if (Array.isArray(options.excludeCredentials)) {
    options.excludeCredentials = options.excludeCredentials.map((c) => ({
      ...c,
      id: typeof c.id === 'string' ? base64URLToBuffer(c.id) : c.id,
    }));
  }
  return { publicKey: options };
}

export function prepareRequestOptions(rawOptions) {
  if (typeof window !== 'undefined' && typeof window.PublicKeyCredential?.parseRequestOptionsFromJSON === 'function') {
    try {
      return { publicKey: window.PublicKeyCredential.parseRequestOptionsFromJSON(rawOptions) };
    } catch {
      // Fallback
    }
  }
  const options = { ...rawOptions };
  if (typeof options.challenge === 'string') {
    options.challenge = base64URLToBuffer(options.challenge);
  }
  if (Array.isArray(options.allowCredentials)) {
    options.allowCredentials = options.allowCredentials.map((c) => ({
      ...c,
      id: typeof c.id === 'string' ? base64URLToBuffer(c.id) : c.id,
    }));
  }
  return { publicKey: options };
}

// --- Credential Serializers ---

export function serializeCreationResponse(credential) {
  if (typeof credential.toJSON === 'function') {
    return credential.toJSON();
  }
  return {
    id: credential.id,
    rawId: bufferToBase64URL(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64URL(credential.response.clientDataJSON),
      attestationObject: bufferToBase64URL(credential.response.attestationObject),
      transports: typeof credential.response.getTransports === 'function' ? credential.response.getTransports() : undefined,
    },
    authenticatorAttachment: credential.authenticatorAttachment,
    clientExtensionResults: credential.getClientExtensionResults?.() || {},
  };
}

export function serializeAuthenticationResponse(credential) {
  if (typeof credential.toJSON === 'function') {
    return credential.toJSON();
  }
  return {
    id: credential.id,
    rawId: bufferToBase64URL(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64URL(credential.response.clientDataJSON),
      authenticatorData: bufferToBase64URL(credential.response.authenticatorData),
      signature: bufferToBase64URL(credential.response.signature),
      userHandle: credential.response.userHandle ? bufferToBase64URL(credential.response.userHandle) : null,
    },
    authenticatorAttachment: credential.authenticatorAttachment,
    clientExtensionResults: credential.getClientExtensionResults?.() || {},
  };
}

// --- High-Level Ceremonies ---

export async function registerPasskey(deviceNickname = 'Passkey') {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported on this browser or device.');
  }

  // 1. Fetch registration options from backend
  const { data: optResp } = await httpClient.instance.post('/api/core/auth/passkeys/register/options');
  const creationOptions = prepareCreationOptions(optResp.options);

  // 2. Browser ceremony
  const credential = await navigator.credentials.create(creationOptions);
  if (!credential) {
    throw new Error('Passkey creation was aborted or cancelled by the user.');
  }

  // 3. Verify & persist on backend
  const payload = {
    credential: serializeCreationResponse(credential),
    challenge: optResp.challenge,
    deviceNickname,
  };
  const { data: verifyResp } = await httpClient.instance.post('/api/core/auth/passkeys/register/verify', payload);
  return verifyResp;
}

export async function loginWithPasskey(guestSessionToken = null) {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported on this browser or device.');
  }

  // 1. Fetch authentication options
  const { data: optResp } = await httpClient.instance.post('/api/core/auth/passkeys/authenticate/options');
  const requestOptions = prepareRequestOptions(optResp.options);

  // 2. Browser ceremony
  const assertion = await navigator.credentials.get(requestOptions);
  if (!assertion) {
    throw new Error('Passkey login was cancelled by the user.');
  }

  // 3. Verify on backend
  const payload = {
    credential: serializeAuthenticationResponse(assertion),
    challenge: optResp.challenge,
    guestSessionToken: guestSessionToken || undefined,
  };
  const { data: authResp } = await httpClient.instance.post('/api/core/auth/passkeys/authenticate/verify', payload);
  return authResp;
}
